import { Injectable } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, UntypedFormArray, UntypedFormControl, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { customServicesValidators, excludeWhitespaceOnly, LatitudeValidators, LongitudeValidators } from './validators';
import { Location } from '../../../../models/locations-models/location';
import { EDITABLE_FIELD_IDS, REQUIRED_FIELDS } from '../../constants';
import { LSCField } from '../../../../models/LSCFields';
import { memoize } from '../../../../core/decorators/memoize';
import { cloneDeep } from 'lodash';
import { SocialMediaUrl } from '../../../../models/locations-models/social-media-urls';
import { CustomServicesTControl, EditLocationTControl, ImportedCoordinatesTControl, SocialMediaUrlsTControl, StatusTControl } from '../../../../models/locations-models/edit-location-form';

export type InputValueType = string | number | null | undefined;

/**
 * Falsy value util
 * @param val
 */
const isNullOrUndefined = (val: any) => {
  return val === null || typeof val === 'undefined';
};

@Injectable({
  providedIn: 'root',
})
export class EditLocationService {
  constructor(private fb: FormBuilder) {
  }

  /**
   * Perform proper value comparison.
   * @param initial
   * @param current
   */
  @memoize()
  hasValueTrulyChanged(initial: InputValueType, current: InputValueType): boolean {
    // Cover the edge case of [null, ''] when the form starts off with `null`, the user
    // types something and then deletes it, resulting in `''`.
    // ngForm considers this a `dirty` change but we do not.
    if ([initial, current].some(isNullOrUndefined)) {
      return Boolean(initial) !== Boolean(current);
    }

    // otherwise, check for deep equality
    return initial !== current;
  }

  /**
   * Checks required rows / fields.
   * @param rowName
   * @private
   */
  @memoize()
  private static isFieldRequired(rowName: string): boolean {
    return (REQUIRED_FIELDS as string[]).includes(rowName);
  }

  /**
   * Iterate the editable fields and create form controls for each.
   * @param location
   * @param formGroup
   */
  addFormControls(location: Location, formGroup: FormGroup) {
    if (!location) {
      return;
    }

    const editableFields = EDITABLE_FIELD_IDS
        // remove all status-fields and add one single group called 'status' instead
        .filter(field => ![LSCField.businessStatus, LSCField.managementStatus].includes(field))
        .concat([LSCField.status]);

    // create form-controls for all fields
    editableFields.forEach((row) => {
      this.addIndividualFormControl(location, formGroup, (row as keyof EditLocationTControl));
    });
  }

  /**
   * Depending on the row, the validators vary.
   * @param location
   * @param formGroup
   * @param rowName
   */
  private addIndividualFormControl(location: Location, formGroup: FormGroup<EditLocationTControl>, rowName: keyof EditLocationTControl) {

    const {
      coordinateImport,
      businessStatus,
      managementStatus,
      customServices,
      socialMediaUrls,
    } = location;

    let newControl: FormGroup<ImportedCoordinatesTControl | null> |
                    FormGroup<StatusTControl | null> |
                    FormGroup<SocialMediaUrlsTControl | null> |
                    FormArray<FormGroup<CustomServicesTControl | null>> |
                    FormControl<string | null>;


    switch (rowName) {
      case LSCField.coordinateImport:
        newControl = this.fb.group(
            {
              'latitude': [coordinateImport?.latitude, LatitudeValidators
                  .concat(coordinateImport?.longitude ? Validators.required : [])],
              'longitude': [coordinateImport?.longitude, LongitudeValidators
                  .concat(coordinateImport?.latitude ? Validators.required : [])],
            },
        );
        break;
      case 'status':
        newControl = this.fb.group(
            {
              'businessStatus': [businessStatus, [Validators.required]],
              'managementStatus': [managementStatus, [Validators.required]],
            },
        );
        break;
      case LSCField.socialMediaUrls:
        newControl = this.fb.group(
            {
              [SocialMediaUrl.FACEBOOK]: [socialMediaUrls?.url_facebook ? socialMediaUrls?.url_facebook : null],
              [SocialMediaUrl.INSTAGRAM]: [socialMediaUrls?.url_instagram ? socialMediaUrls?.url_instagram : null],
              [SocialMediaUrl.LINKEDIN]: [socialMediaUrls?.url_linkedin ? socialMediaUrls?.url_linkedin : null],
              [SocialMediaUrl.PINTEREST]: [socialMediaUrls?.url_pinterest ? socialMediaUrls?.url_pinterest : null],
              [SocialMediaUrl.TIKTOK]: [socialMediaUrls?.url_tiktok ? socialMediaUrls?.url_tiktok : null],
              [SocialMediaUrl.TWITTER]: [socialMediaUrls?.url_twitter ? socialMediaUrls?.url_twitter : null],
              [SocialMediaUrl.YOUTUBE]: [socialMediaUrls?.url_youtube ? socialMediaUrls?.url_youtube : null],
            },
        );
        break;
      case LSCField.customServices:  // these need a dynamic form-array
        newControl = this.fb.array([]) as unknown as FormArray<FormGroup<CustomServicesTControl>>;
        customServices?.forEach(({ serviceName, serviceDescription, serviceCategoryId }) => {
          (newControl as FormArray<FormGroup<CustomServicesTControl>>).push(this.fb.group(
              {
                'serviceName': [
                  serviceName,
                  customServicesValidators.serviceName,
                ],
                'serviceDescription': [
                  serviceDescription,
                  customServicesValidators.serviceDescription,
                ],
                'serviceCategoryId': [
                  serviceCategoryId,
                  customServicesValidators.serviceCategoryId,
                ],
              },
          ));
        });
        break;
      default: {
        // default validation
        const validators: ValidatorFn[] = [excludeWhitespaceOnly];

        if (EditLocationService.isFieldRequired(rowName)) {
          validators.push(Validators.required);
        }
        newControl = this.fb.control(location[rowName], validators);
      }
    }

    //add the new formControl to the formGroup from args
    formGroup.addControl(rowName, newControl);
  }

  /**
   * Construct a new form group instance.
   */
  createCustomServiceFormGroup() {
    return this.fb.group({
      'serviceName': [null, customServicesValidators.serviceName],
      'serviceDescription': [null, customServicesValidators.serviceDescription],
      'serviceCategoryId': [null, customServicesValidators.serviceCategoryId],
    }) as FormGroup<CustomServicesTControl>;
  }

  /**
   * Converts the formGroup and its inputs to a Location that can be sent to the BE.
   * @param formEntry Form group
   * @param recursionLevel
   */
  formGroupToLocation(formEntry: UntypedFormGroup | UntypedFormControl, recursionLevel = 0): Location {
    if (!('controls' in formEntry)) {
      if ((formEntry as unknown as UntypedFormControl).value === '') {
        // empty strings need to be converted to `null`
        (formEntry as unknown as UntypedFormControl).patchValue(null);
      }
      return;
    }

    for (const [_, control] of Object.entries((formEntry as unknown as UntypedFormGroup).controls)) {
      if (!('controls' in control)) {
        // we're dealing w/ simple form controls
        this.formGroupToLocation(control as UntypedFormControl, recursionLevel + 1);
      } else if (Array.isArray((control as UntypedFormArray)?.controls)) {
        // take case of form arrays (e.g. customServices)
        for (const entryFormGroup of (control as UntypedFormArray)?.controls) {
          this.formGroupToLocation(entryFormGroup as UntypedFormGroup, recursionLevel + 1);
        }
      } else if (
          typeof (control as UntypedFormGroup)?.controls !== 'undefined' &&
          !Array.isArray((control as UntypedFormGroup)?.controls)
      ) {
        // we're dealing w/ nested form controls (e.g. coordinateImport)
        for (const individualControl of Object.values((control as UntypedFormGroup).controls)) {
          this.formGroupToLocation(individualControl as UntypedFormControl, recursionLevel + 1);
        }
      }
    }

    if (recursionLevel === 0) {
      const topLevelEntry = formEntry as unknown as UntypedFormGroup;
      const finalValues = cloneDeep(topLevelEntry.value) as Location;

      // flatten the statuses onto the top level
      finalValues.businessStatus = topLevelEntry.get('status').get('businessStatus').value;
      finalValues.managementStatus = topLevelEntry.get('status').get('managementStatus').value;
      return finalValues;
    }
  }

  /**
   * Checks for two locations if they have the same values for all editable fields.
   * @param initialLocation
   * @param editedLocation
   */
  areEditableValuesDifferent(initialLocation: Location, editedLocation: Location): boolean {
    for (const editableField of EDITABLE_FIELD_IDS) {
      // special case - when coordinateImport form field is initiated and initialLocation.coordinateImport = null,
      // editedLocation.coordinateImport, the initialized form field value, will be {latitude: null, longitude: null}
      // which would result in a false positive had we compared stringified JSONs
      if (editableField == LSCField.coordinateImport) {
        let editedLat = editedLocation?.coordinateImport?.latitude;
        let editedLng = editedLocation?.coordinateImport?.longitude;

        // when user deletes field value and types a new one,
        // it'll be of type 'string' which could cause another false positive returned by
        // hasValueTrulyChanged() which checks for deep equality
        if (editedLat && typeof editedLat === 'string') {
          editedLat = parseFloat(editedLat);
        }

        if (editedLng && typeof editedLng === 'string') {
          editedLng = parseFloat(editedLng);
        }

        if (this.hasValueTrulyChanged(initialLocation?.coordinateImport?.latitude, editedLat) ||
            this.hasValueTrulyChanged(initialLocation?.coordinateImport?.longitude, editedLng)) {
          return true;
        }
      } else {
        if (JSON.stringify(initialLocation[editableField]) !== JSON.stringify(editedLocation[editableField])) {
          return true;
        }
      }
    }

    return false;
  }

}
